import React, { useState } from 'react';
import { slugify } from '../../utils/seoFormHelpers';
import ImageUrlField from './ImageUrlField';
import './SeoFieldsForm.css';

const LOCALES = ['en', 'nl', 'es', 'fr', 'de', 'it', 'pt', 'el'];

const SeoFieldsForm = ({
  values = {},
  onChange,
  showLocaleTabs = false,
  variant = 'light',
  title = 'SEO Settings',
  slugPrefix = '',
  autoSlugFrom,
}) => {
  const [activeLocale, setActiveLocale] = useState('en');
  const isDark = variant === 'dark';

  const set = (field, val) => onChange({ ...values, [field]: val });

  const setLocaleField = (field, val) => {
    const seoByLocale = { ...(values.seoByLocale || {}) };
    seoByLocale[activeLocale] = { ...(seoByLocale[activeLocale] || {}), [field]: val };
    onChange({ ...values, seoByLocale });
  };

  const localeValues = values.seoByLocale?.[activeLocale] || {};

  const copyFromEn = () => {
    const en = values.seoByLocale?.en || {};
    const seoByLocale = { ...values.seoByLocale };
    LOCALES.forEach((loc) => {
      if (loc !== 'en') seoByLocale[loc] = { ...en };
    });
    onChange({ ...values, seoByLocale });
  };

  const handleSlugInput = (raw) => {
    const slug = raw
      .toLowerCase()
      .replace(/[^a-z0-9-]/g, '-')
      .replace(/-+/g, '-');
    onChange({ ...values, slug, slugLocked: true });
  };

  const regenerateFromName = () => {
    if (autoSlugFrom === undefined) return;
    onChange({ ...values, slug: slugify(autoSlugFrom), slugLocked: false });
  };

  const previewSlug = values.slug || slugify(autoSlugFrom || '') || 'your-product-name';
  const previewPath = `${slugPrefix || '/'}${previewSlug}`;

  return (
    <div className={`seo-fields-form ${isDark ? 'seo-fields-form-dark' : ''}`}>
      <h3>{title}</h3>

      {showLocaleTabs && (
        <div className="seo-locale-tabs">
          {LOCALES.map((loc) => (
            <button key={loc} type="button" className={activeLocale === loc ? 'active' : ''} onClick={() => setActiveLocale(loc)}>
              {loc.toUpperCase()}
            </button>
          ))}
          <button type="button" className="copy-en-btn" onClick={copyFromEn}>Copy from EN</button>
        </div>
      )}

      <div className="seo-field">
        <label>SEO Title <span className="char-count">({(showLocaleTabs ? localeValues.seoTitle : values.seoTitle || values.seoTitle)?.length || 0}/60)</span></label>
        <input
          type="text"
          value={showLocaleTabs ? (localeValues.seoTitle || '') : (values.seoTitle || values.metaTitle || '')}
          onChange={(e) => showLocaleTabs ? setLocaleField('seoTitle', e.target.value) : set('seoTitle', e.target.value)}
          placeholder="Page title for search engines"
        />
      </div>

      <div className="seo-field">
        <label>Meta Description <span className="char-count">({(showLocaleTabs ? localeValues.metaDescription : values.metaDescription)?.length || 0}/160)</span></label>
        <textarea
          rows={3}
          value={showLocaleTabs ? (localeValues.metaDescription || '') : (values.metaDescription || '')}
          onChange={(e) => showLocaleTabs ? setLocaleField('metaDescription', e.target.value) : set('metaDescription', e.target.value)}
        />
      </div>

      {!showLocaleTabs && (
        <>
          <div className="seo-field seo-slug-field">
            <label>URL Slug</label>
            <div className="slug-permalink-box">
              <span className="slug-permalink-label">Permalink preview</span>
              <code className="slug-permalink-path">{previewPath}</code>
            </div>
            <div className="slug-input-row">
              <input
                type="text"
                value={values.slug || ''}
                onChange={(e) => handleSlugInput(e.target.value)}
                placeholder="auto-generated-from-name"
              />
              {autoSlugFrom !== undefined && (
                <button type="button" className="slug-regen-btn" onClick={regenerateFromName} title="Regenerate slug from name">
                  From name
                </button>
              )}
            </div>
            <small className="slug-hint">
              {values.slugLocked
                ? 'Slug is manually edited. Click “From name” to sync it with the product name again.'
                : 'Slug auto-updates from the name (like WordPress). Edit the field to customize.'}
            </small>
          </div>

          <div className="seo-field">
            <label>Canonical URL</label>
            <input type="url" value={values.canonicalUrl || ''} onChange={(e) => set('canonicalUrl', e.target.value)} />
          </div>

          <div className="seo-row">
            <label><input type="checkbox" checked={values.robotsIndex !== false} onChange={(e) => set('robotsIndex', e.target.checked)} /> Index</label>
            <label><input type="checkbox" checked={values.robotsFollow !== false} onChange={(e) => set('robotsFollow', e.target.checked)} /> Follow</label>
            <label><input type="checkbox" checked={values.includeInSitemap !== false} onChange={(e) => set('includeInSitemap', e.target.checked)} /> Include in Sitemap</label>
          </div>

          <h4>Open Graph</h4>
          <div className="seo-field"><label>OG Title</label><input type="text" value={values.ogTitle || ''} onChange={(e) => set('ogTitle', e.target.value)} /></div>
          <div className="seo-field"><label>OG Description</label><textarea rows={2} value={values.ogDescription || ''} onChange={(e) => set('ogDescription', e.target.value)} /></div>
          <ImageUrlField
            label="OG Image"
            value={values.ogImage || ''}
            onChange={(url) => set('ogImage', url)}
            folder="seo"
            placeholder="https://... or upload"
          />

          <h4>Twitter</h4>
          <div className="seo-field"><label>Twitter Title</label><input type="text" value={values.twitterTitle || ''} onChange={(e) => set('twitterTitle', e.target.value)} /></div>
          <div className="seo-field"><label>Twitter Description</label><textarea rows={2} value={values.twitterDescription || ''} onChange={(e) => set('twitterDescription', e.target.value)} /></div>
          <ImageUrlField
            label="Twitter Image"
            value={values.twitterImage || ''}
            onChange={(url) => set('twitterImage', url)}
            folder="seo"
            placeholder="https://... or upload"
          />

          <div className="seo-field">
            <label>Featured Image Alt Text</label>
            <input type="text" value={values.featuredImageAlt || ''} onChange={(e) => set('featuredImageAlt', e.target.value)} />
          </div>

          <div className="seo-field">
            <label>Schema Type</label>
            <select value={values.schemaType || 'none'} onChange={(e) => set('schemaType', e.target.value)}>
              <option value="none">None</option>
              <option value="Organization">Organization</option>
              <option value="Article">Article</option>
              <option value="FAQ">FAQ</option>
              <option value="Product">Product</option>
              <option value="LocalBusiness">Local Business</option>
            </select>
          </div>
        </>
      )}
    </div>
  );
};

export default SeoFieldsForm;
